import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Inbox, Check, X, Mail, Bell, BellOff } from "lucide-react";
import { shouldNotify } from "@/lib/notifPrefs";

export function ChefHireInbox() {
  const [hires, setHires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyPerm, setNotifyPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  const knownIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/chef-hire/mine");
      // Detect new hires vs known set → browser notification
      if (!firstLoadRef.current && Notification?.permission === "granted" && shouldNotify("hires")) {
        const newOnes = data.filter((h) => !knownIdsRef.current.has(h.id) && h.status === "new");
        newOnes.slice(0, 3).forEach((h) => {
          try {
            new Notification("New hire request", {
              body: `${h.contact_name} · ${new Date(h.event_date).toLocaleDateString()} · ${h.hours}h`,
              icon: "/favicon.ico",
              tag: `hire-${h.id}`,
            });
          } catch (err) { console.error("Notification failed:", err); }
        });
      }
      knownIdsRef.current = new Set(data.map((h) => h.id));
      firstLoadRef.current = false;
      setHires(data);
    } catch (err) { console.error("Hires load failed:", err); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableNotifs = async () => {
    if (typeof Notification === "undefined") { toast.error("Notifications not supported"); return; }
    const perm = await Notification.requestPermission();
    setNotifyPerm(perm);
    if (perm === "granted") toast.success("You'll be pinged the moment a hire lands");
  };

  const decide = async (id, status) => {
    try {
      await api.patch(`/chef-hire/${id}`, { status });
      toast.success(`Request ${status}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const notifBtn = notifyPerm === "granted" ? (
    <span className="tick-label inline-flex items-center gap-1 text-[#FF3B30]"><Bell size={11} /> Live alerts on</span>
  ) : notifyPerm === "denied" ? (
    <span className="tick-label inline-flex items-center gap-1 text-neutral-500"><BellOff size={11} /> Blocked</span>
  ) : (
    <button data-testid="hire-notif-enable" onClick={enableNotifs} className="btn-outline inline-flex items-center gap-1"><Bell size={12} /> Enable live alerts</button>
  );

  if (loading && hires.length === 0) return null;
  if (hires.length === 0) {
    return (
      <div className="space-y-3">
        <div className="flex justify-end">{notifBtn}</div>
        <div data-testid="hires-empty" className="border hairline p-6 text-center">
          <Inbox size={20} className="mx-auto text-neutral-500" />
          <p className="text-neutral-400 text-sm mt-2">No hire requests yet.</p>
          <p className="text-neutral-500 text-xs mt-1">Fill in your profile at /cook/profile — clients discover you from the "Find a Chef" directory.</p>
        </div>
      </div>
    );
  }

  const badge = {
    new: "border-yellow-500 text-yellow-500",
    accepted: "border-[#FF3B30] text-[#FF3B30]",
    declined: "border-neutral-600 text-neutral-500 line-through",
  };

  return (
    <div>
      <div className="flex justify-end mb-3">{notifBtn}</div>
      <div data-testid="hires-list" className="border hairline">
        {hires.map((h) => (
          <div key={h.id} data-testid={`hire-${h.id}`} className="p-5 border-b hairline last:border-b-0">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-3">
                  <span className="font-display text-xl font-bold">{h.contact_name}</span>
                  <span className={`inline-block px-2 py-0.5 border text-[10px] uppercase tracking-widest ${badge[h.status] || ""}`}>{h.status}</span>
                </div>
                <div className="tick-label mt-1 flex items-center gap-1.5">
                  <Mail size={11} /> <a href={`mailto:${h.contact_email}`} className="text-[#FF3B30] normal-case tracking-normal">{h.contact_email}</a>
                </div>
                <div className="mt-3 font-mono text-sm text-neutral-300">
                  {new Date(h.event_date).toLocaleString()} · {h.hours}h
                  {h.budget && <span className="text-[#FF3B30]"> · budget ${h.budget}</span>}
                </div>
                {h.notes && <p className="text-sm text-neutral-400 mt-2 whitespace-pre-wrap">{h.notes}</p>}
              </div>
              {h.status === "new" && (
                <div className="flex gap-2">
                  <button data-testid={`hire-accept-${h.id}`} onClick={() => decide(h.id, "accepted")} className="btn-signal inline-flex items-center gap-1"><Check size={13} /> Accept</button>
                  <button data-testid={`hire-decline-${h.id}`} onClick={() => decide(h.id, "declined")} className="btn-outline inline-flex items-center gap-1"><X size={13} /> Decline</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
